/**
 * Supressão do banner "Precisa da sua atenção" por TARP (GUPM-221). TS puro.
 *
 * O bloco do dashboard é um **pedido de ação**, não um espelho do estado dos
 * instrumentos — esse espelho já existe no Resumo Técnico, no mapa e no semáforo.
 * Repetir um instrumento cuja TARP já foi aberta treina o operador a ignorar o
 * bloco, que é justamente onde a emergência aparece. Por isso, uma vez que a ação
 * está registrada e em curso, o card sai.
 *
 * Duas garantias impedem que a regra esconda risco real:
 *  1. **escalada nunca é suprimida** — leitura acima do nível que a TARP tratou
 *     volta ao bloco em qualquer estado, inclusive dentro da carência;
 *  2. **prazo de baixa vencido volta ao bloco** — o prazo ANM por nível
 *     (48h/24h/12h) não pode ser mascarado por uma TARP parada no RT.
 *
 * Rascunho `EM_ABERTURA` não suprime: pode ser descartado e não registra ação —
 * mesma exclusão que `contarTarpsPorBarragem` já faz no status da estrutura.
 *
 * GUPM-306 — e a leitura que VENCE a carência precisa estar atual. A carência foi
 * desenhada para ser re-armada pela próxima leitura; com o feed morto, ela congelava no
 * último veredito e pedia ação para sempre. O limite de idade vale só nesse caminho: nos
 * outros a ação ainda não foi tomada, e limitá-los viraria silêncio.
 */
import type { TarpLevel } from '../types';
import type { FlowState } from '../tarp/flow';
import type { TarpRecordStatus } from '../tarp/status';
import { nivelEfetivoVisivel } from '../tarp/nivel-efetivo';
import { calcPrazoBaixa } from '../tarp/prazo';
import { leituraForaDaJanela } from '../instrumentos/perda-de-sinal';

/**
 * Janela de silêncio após a baixa do RT. Encerrada a TARP, quem decide quando o
 * instrumento normaliza é o plano de ação — não a próxima leitura. Só depois
 * desta janela uma leitura que ainda acusa o nível volta a pedir ação.
 */
export const CARENCIA_POS_BAIXA_H = 24;

/** Instrumento anômalo candidato ao bloco (shape mínimo, desacoplado de `Instrument`). */
export interface AnomaliaLike {
  instrumentId: string;
  level: TarpLevel;
  /** Timestamp da leitura que gerou o nível — mede a carência pós-baixa. */
  ts: Date;
  /**
   * GUPM-306 — tipo do instrumento no enum do BANCO (`TILT`, não `TIL`). Mede se a
   * leitura que venceria a carência ainda está atual.
   *
   * **Obrigatório de propósito**, e sem default. Como opcional, o chamador que
   * esquecesse de passar voltaria ao comportamento congelado sem nada acusar — e este
   * app já pagou duas vezes por regra ligada ponto a ponto e esquecida ponto a ponto
   * (GUPM-270, e o `semSinal` obrigatório do GUPM-303). Obrigatório faz o `tsc`
   * enumerar os chamadores e o build quebrar até que cada um passe o fato.
   */
  tipo: string;
  /**
   * GUPM-306 — o que este card AFIRMA: uma leitura fora de faixa, ou a ausência de
   * leitura. Obrigatório e sem default.
   *
   * Existe porque o limite de frescor só faz sentido sobre `'leitura'`: ele pergunta
   * "esse número ainda é medição atual?". Num card de perda de sinal, o `ts` ser velho
   * **é o que o card afirma** — limitá-lo por idade suprimiria exatamente o alarme que
   * ele existe para dar.
   *
   * Uma versão anterior deste card não tinha o campo e se apoiava numa prova algébrica:
   * como `computeSemSinal` grava `ts` e `silencioDesde` com o MESMO valor, alcançar o
   * caminho da carência implicaria `episodioDesde > closedAt`, capturado antes. A prova
   * era correta e dependia de uma invariante que **nenhum tipo impunha** — bastaria um
   * chamador futuro achar mais preciso usar a última leitura real como `ts` para a
   * guarda cair calada. Discriminante explícito troca a prova por regra.
   */
  origem: 'leitura' | 'perda-de-sinal';
  /**
   * GUPM-303 — início do EPISÓDIO de perda de sinal, quando a anomalia é de perda de
   * sinal. Presente só nesse caso.
   *
   * Existe porque a carência de 24h, sozinha, esconderia falha NOVA: o instrumento cai,
   * é tratado, tem baixa, **volta a publicar** e cai de novo duas horas depois. A leitura
   * congelada não distingue os dois casos — o que distingue é o silêncio ter começado
   * DEPOIS da baixa (`episodioDesde > closedAt`), que só é possível se o instrumento
   * publicou nesse intervalo.
   */
  episodioDesde?: Date;
}

/** Registro TARP no mínimo necessário para decidir a supressão. */
export interface TarpParaBanner {
  instrumentId: string | null;
  status: TarpRecordStatus;
  tarpCode: string;
  flowState: FlowState | null;
  openedAt: string;
  /** Encaminhamento ao RT — início da contagem do prazo de baixa. */
  concludedAt: string | null;
  /** Baixa do RT — início da carência. */
  closedAt: string | null;
}

/** Por que o card aparece (ou não). Explicita a decisão para teste e depuração. */
export type MotivoBanner =
  | 'sem-tarp'
  | 'escalada'
  | 'prazo-estourado'
  | 'carencia-vencida'
  | 'tarp-ativa'
  | 'carencia-pos-baixa'
  /** GUPM-303 — perda de sinal que começou DEPOIS da baixa: falha nova, não repetição. */
  | 'episodio-novo'
  /** GUPM-306 — a leitura que venceria a carência está fora da janela de frescor. */
  | 'leitura-congelada';

export interface DecisaoBanner {
  exibir: boolean;
  motivo: MotivoBanner;
}

const SEVERIDADE: Record<TarpLevel, number> = {
  NORMAL: 0,
  ATENCAO: 1,
  ALERTA: 2,
  EMERGENCIA: 3,
};

const HORA_MS = 3_600_000;

const ms = (iso: string | null): number => (iso ? new Date(iso).getTime() : 0);

/** Protocolos que tratam perda de sinal: os `TARP-PS-*` e a TILT-EMG (mesmo fluxo da PS-TILT). */
const TRATA_PERDA_DE_SINAL = (code: string): boolean =>
  code.startsWith('TARP-PS-') || code === 'TARP-TILT-EMG';

/**
 * TARP que governa a decisão para este instrumento: a ativa mais recente (por
 * abertura) e, na falta de ativa, a encerrada mais recente (por baixa). Rascunho
 * `EM_ABERTURA` fica de fora. `null` quando não há nenhuma.
 */
function tarpRelevante(
  tarps: readonly TarpParaBanner[],
  instrumentId: string,
  origem: AnomaliaLike['origem'],
): TarpParaBanner | null {
  const doInstrumento = tarps.filter(
    (t) => t.instrumentId === instrumentId && t.status !== 'EM_ABERTURA'
      // C-88 — o card de SEM SINAL só é tratado por TARP que trata perda de sinal. Desde
      // o C-88 o tiltímetro também abre TARP de nível (INST-AT/AL), cujo fluxo genérico não
      // pergunta pelo AAS nem pela ruptura: com ela aberta, o sensor que emudece tem de
      // continuar pedindo ação. A TILT-EMG conta porque é o mesmo fluxo da PS-TILT.
      && (origem !== 'perda-de-sinal' || TRATA_PERDA_DE_SINAL(t.tarpCode)),
  );
  if (doInstrumento.length === 0) return null;

  const ativas = doInstrumento.filter((t) => t.status !== 'ENCERRADA');
  if (ativas.length > 0) {
    return ativas.reduce((a, b) => (ms(b.openedAt) > ms(a.openedAt) ? b : a));
  }
  return doInstrumento.reduce((a, b) => (ms(b.closedAt) > ms(a.closedAt) ? b : a));
}

/**
 * Decide se um instrumento anômalo deve aparecer no bloco "Precisa da sua
 * atenção", dado o histórico de TARPs dele.
 */
export function avaliarBannerAnomalia(
  anomalia: AnomaliaLike,
  tarps: readonly TarpParaBanner[],
  agora: Date,
): DecisaoBanner {
  const tarp = tarpRelevante(tarps, anomalia.instrumentId, anomalia.origem);
  if (!tarp) return { exibir: true, motivo: 'sem-tarp' };

  const nivelTarp = nivelEfetivoVisivel(tarp);
  if (SEVERIDADE[anomalia.level] > SEVERIDADE[nivelTarp]) {
    return { exibir: true, motivo: 'escalada' };
  }

  if (tarp.status !== 'ENCERRADA') {
    // Antes do encaminhamento ao RT não há prazo correndo: o operador ainda
    // executa o checklist, e a ação está em curso.
    const prazo = calcPrazoBaixa(tarp.concludedAt, nivelTarp, agora.toISOString());
    if (prazo?.estourado) return { exibir: true, motivo: 'prazo-estourado' };
    return { exibir: false, motivo: 'tarp-ativa' };
  }

  // ENCERRADA sem `closedAt` não deveria existir; sem a âncora da carência, o
  // seguro é pedir ação em vez de silenciar por tempo indeterminado.
  if (!tarp.closedAt) return { exibir: true, motivo: 'carencia-vencida' };

  // GUPM-303 — episódio de perda de sinal iniciado APÓS a baixa é falha nova: o
  // instrumento voltou a publicar (senão o silêncio teria começado antes) e caiu de novo.
  // A carência existe para não repetir a MESMA anomalia; repetir não é o caso aqui.
  if (anomalia.episodioDesde && anomalia.episodioDesde.getTime() > ms(tarp.closedAt)) {
    return { exibir: true, motivo: 'episodio-novo' };
  }

  const fimDaCarencia = ms(tarp.closedAt) + CARENCIA_POS_BAIXA_H * HORA_MS;
  // Compara com o instante da LEITURA (não com o relógio): "a próxima leitura
  // após 24h" só conta quando ela de fato chega.
  if (anomalia.ts.getTime() < fimDaCarencia) {
    return { exibir: false, motivo: 'carencia-pos-baixa' };
  }

  // GUPM-306 — a leitura que vence a carência precisa estar ATUAL. A comparação acima é
  // com o instante da leitura, e não com o relógio, porque "a próxima leitura após 24h só
  // conta quando ela de fato chega". Isso pressupõe que leituras continuam chegando; com o
  // feed morto, a última decisão vira permanente. O INA-12A tem leitura de 20/08 12:00Z,
  // posterior ao fim da carência de uma TARP encerrada em 18/08 14:03Z, e por isso pediu
  // ação todos os dias desde então — 4 TARP-INST-EMG abertas e encerradas, e a oferta
  // continuou.
  //
  // Só ESTE caminho é limitado por idade. `sem-tarp`, `escalada`, `prazo-estourado` e o
  // `!closedAt` defensivo seguem sem limite: neles a ação ainda não foi tomada, e é aí que
  // um limite geral viraria silêncio. Aqui a ação já foi tomada e encerrada, e uma leitura
  // congelada não é fato novo que a justifique de novo.
  //
  // E só sobre `origem: 'leitura'`: num card de perda de sinal o `ts` velho é o próprio
  // conteúdo do alarme, não prova de dado congelado.
  if (anomalia.origem === 'leitura' && leituraForaDaJanela(anomalia.ts, anomalia.tipo, agora)) {
    return { exibir: false, motivo: 'leitura-congelada' };
  }
  return { exibir: true, motivo: 'carencia-vencida' };
}
